import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import moment from 'moment-timezone'
import { X } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose
} from '../../ui/drawer'
import { Button } from '../../ui/button'
import { Switch } from '../../ui/switch'
import { Label } from '../../ui/label'
import LoadingSpinner from '../../leevo_ui/LoadingSpinner/LoadingSpinner'
import { requestCreator } from '../../../utilities/requests'
import { mapKlassPayload } from '../../../utilities/apiPayloadMappers'
import ClassScheduleSection from './ClassScheduleSection'
import SportCurriculumSection from './SportCurriculumSection'
import CoachingSettingsSection from './CoachingSettingsSection'
import EnrollmentSection from './EnrollmentSection'
import ClassPackagesSection from './ClassPackagesSection'
import WaitlistManagementSection from '../WaitlistManagementSection/WaitlistManagementSection'
import { extractErrorMessage } from '../../../utilities/errorHelpers'

const { get: getKlass, put: updateKlass, cancel: cancelKlassRequests } = requestCreator()
const { get: getSkillLevels, cancel: cancelSkillLevelsRequests } = requestCreator()
const { get: getKlassPackages, cancel: cancelKlassPackagesRequests } = requestCreator()
const { get: getCoaches, cancel: cancelCoachesRequests } = requestCreator()

function ClassEditDrawer({ open, onOpenChange, klassId, facility, onSuccess }) {
  const facilityId = facility?.id
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const [klassData, setKlassData] = useState(null)
  const [allCoaches, setAllCoaches] = useState([])
  const [allSkillLevels, setAllSkillLevels] = useState([])
  const [formData, setFormData] = useState({
    dates: [],
    coaches: [],
    supportedSkillLevels: [],
    students: [],
    klassPackages: [],
    maxStudents: null,
    sport: null,
    curriculumId: null,
    curriculumName: null,
    suppressWaitlistNotifications: false
  })

  useEffect(() => {
    if (open && klassId) {
      loadKlassData()
    }

    return () => {
      cancelKlassRequests()
      cancelSkillLevelsRequests()
      cancelKlassPackagesRequests()
      cancelCoachesRequests()
    }
  }, [open, klassId, facilityId])

  async function loadKlassData() {
    setIsLoading(true)
    try {
      const [klass, skillLevels, klassPackages, coaches] = await Promise.all([
        getKlass(`/api/facilities/${facilityId}/klasses/${klassId}`, {
          params: {
            include: 'coaches,supported_skill_levels,klass_packages,students',
            fields: {
              klass:
                'dayOfWeek,timeOfDay,startDate,endDate,dates,coaches,supported_skill_levels,maxStudents,students,klass_packages,sport,curriculumId,curriculumName,multiDay,waitlistCount',
              coach: 'fullName,cloudinaryPhotoPublicId',
              skillLevel: 'name,curriculumNames,sport',
              klassPackage: 'title',
              student: 'fullName,preferredName,cloudinaryPhotoId'
            }
          }
        }),
        getSkillLevels(`/api/facilities/${facilityId}/skill_levels`),
        getKlassPackages(`/api/facilities/${facilityId}/class_packages`),
        getCoaches(`/api/facilities/${facilityId}/coaches`, {
          params: { fields: { coach: 'fullName,cloudinaryPhotoPublicId' } }
        })
      ])

      setKlassData(klass)

      const dates = (klass.dates || []).map((date) => ({
        date: moment(date).tz(facility.timeZone),
        isTimeSet: true
      }))

      const formattedCoaches = coaches.map((coach) => ({
        ...coach,
        label: coach.fullName,
        value: coach.id
      }))

      const formattedSkillLevels = skillLevels.map((level) => ({
        ...level,
        label: level.name,
        value: level.id
      }))

      const formattedPackages = klassPackages.map((pkg) => ({
        ...pkg,
        label: pkg.title,
        value: pkg.id,
        selected: klass.klassPackages.some((kp) => kp.id === pkg.id)
      }))

      const formattedStudents = klass.students.map((student) => ({
        ...student,
        selected: true
      }))

      const selectedCoaches = formattedCoaches.filter((c) => klass.coaches.some((kc) => kc.id === c.id))
      const selectedSkillLevels = formattedSkillLevels.filter((sl) =>
        klass.supportedSkillLevels.some((ksl) => ksl.id === sl.id)
      )

      setAllCoaches(formattedCoaches)
      setAllSkillLevels(formattedSkillLevels)

      setFormData({
        dates,
        coaches: selectedCoaches,
        supportedSkillLevels: selectedSkillLevels,
        students: formattedStudents,
        klassPackages: formattedPackages,
        maxStudents: klass.maxStudents,
        sport: klass.sport,
        curriculumId: klass.curriculumId,
        curriculumName: klass.curriculumName
      })

      setIsLoading(false)
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Error loading class data:', error)
        setIsLoading(false)
      }
    }
  }

  function updateFormData(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  async function handleSave() {
    setIsSaving(true)
    try {
      const payload = {
        klass: mapKlassPayload({
          coaches: formData.coaches.map((c) => c.id),
          supportedSkillLevels: formData.supportedSkillLevels.map((sl) => sl.id),
          klassPackages: formData.klassPackages.filter((kp) => kp.selected).map((kp) => kp.id),
          dates: formData.dates.filter((d) => d.isTimeSet).map((d) => d.date.toString()),
          students: formData.students.filter((s) => s.selected).map((s) => s.id),
          maxStudents: formData.maxStudents,
          sport: formData.sport,
          curriculumId: formData.curriculumId,
          suppress_waitlist_notifications: formData.suppressWaitlistNotifications
        })
      }

      await updateKlass(`/api/facilities/${facilityId}/klasses/${klassId}`, payload)

      onSuccess?.('Class updated successfully!')
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving class:', error)
      alert(extractErrorMessage(error))
    } finally {
      setIsSaving(false)
    }
  }

  const drawerTitle = klassData ? `${klassData.dayOfWeek} - ${klassData.timeOfDay}` : 'Edit Class'

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          className="flex flex-col md:!w-[800px] md:!min-w-[800px] md:!max-w-[800px]"
          onKeyDown={(e) => {
            if (e.target.tagName === 'INPUT') {
              e.stopPropagation()
            }
          }}
        >
          <DrawerHeader className="border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <DrawerTitle>{drawerTitle}</DrawerTitle>
              <DrawerClose asChild>
                <Button variant="ghost" size="sm">
                  <X className="w-4 h-4" />
                </Button>
              </DrawerClose>
            </div>
            <DrawerDescription className="sr-only">
              Edit class schedule, coaches, skill levels, enrollment, and packages
            </DrawerDescription>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64 w-full min-w-[700px]">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="p-4 space-y-4">
                <ClassScheduleSection dates={formData.dates} onChange={(dates) => updateFormData('dates', dates)} />

                <SportCurriculumSection
                  facilityId={facilityId}
                  sport={formData.sport}
                  curriculumId={formData.curriculumId}
                  curriculumName={formData.curriculumName}
                  supportedSkillLevels={formData.supportedSkillLevels}
                  onSportChange={(sport) => updateFormData('sport', sport)}
                  onCurriculumChange={(id, name) => {
                    setFormData((prev) => ({ ...prev, curriculumId: id, curriculumName: name }))
                  }}
                  onSkillLevelsChange={(levels) => updateFormData('supportedSkillLevels', levels)}
                />

                <CoachingSettingsSection
                  allCoaches={allCoaches}
                  allSkillLevels={allSkillLevels}
                  coaches={formData.coaches}
                  supportedSkillLevels={formData.supportedSkillLevels}
                  maxStudents={formData.maxStudents}
                  onCoachesChange={(coaches) => updateFormData('coaches', coaches)}
                  onSkillLevelsChange={(levels) => updateFormData('supportedSkillLevels', levels)}
                  onMaxStudentsChange={(max) => updateFormData('maxStudents', max)}
                />

                <EnrollmentSection
                  students={formData.students}
                  maxStudents={formData.maxStudents}
                  onChange={(students) => updateFormData('students', students)}
                  facilityId={facilityId}
                  selectedSkillLevels={formData.supportedSkillLevels}
                />

                {(klassData?.waitlistCount ?? klassData?.waitlist_count ?? 0) > 0 && (
                  <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="suppress-waitlist" className="text-sm font-medium">
                        Suppress waitlist notifications
                      </Label>
                      <p className="text-xs text-gray-500">
                        Turn on if you are reorganizing rosters, not opening spots.
                      </p>
                    </div>
                    <Switch
                      id="suppress-waitlist"
                      checked={formData.suppressWaitlistNotifications}
                      onCheckedChange={(checked) => updateFormData('suppressWaitlistNotifications', checked)}
                    />
                  </div>
                )}

                <WaitlistManagementSection
                  key={klassId}
                  klassId={klassId}
                  facilityId={facilityId}
                  enrolledStudentIds={formData.students
                    .filter((student) => student.selected)
                    .map((student) => student.id)}
                />

                <ClassPackagesSection
                  klassPackages={formData.klassPackages}
                  onChange={(packages) => updateFormData('klassPackages', packages)}
                />
              </div>
            )}
          </div>

          <DrawerFooter className="border-t border-gray-200 flex-shrink-0">
            <div className="flex w-full space-x-2">
              <Button onClick={handleSave} disabled={isSaving || isLoading} className="flex-1">
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline" className="flex-1">
                  Cancel
                </Button>
              </DrawerClose>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}

ClassEditDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  klassId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  facility: PropTypes.object.isRequired,
  onSuccess: PropTypes.func
}

export default ClassEditDrawer
